/**
 * Meldt dat er een nieuwere versie van het portaal klaarstaat.
 *
 * Wie het portaal open heeft staan tijdens een uitrol, blijft de oude code draaien: klikken bínnen
 * het portaal is geen nieuwe laadbeurt. Dat gaat ongemerkt goed tot het scherm iets vraagt wat de
 * server niet meer zo beantwoordt — op 24-08-2026 leverde dat een half uur zoeken op naar een fout
 * die al opgelost was.
 *
 * Er wordt nooit uit zichzelf herladen. Iemand kan midden in een formulier zitten.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Eén waarde voor beide kanten: de bouwstap schrijft het bestandje onder deze naam, en het scherm
// vraagt het op dit pad op. Zouden ze uit elkaar lopen, dan zwijgt de melding voorgoed.
import { VERSIEBESTAND_PAD } from '@/bouw/versiebestand';
import { LAGEN } from '@xxtract/ui';
import { cn } from '@/lib/utils';

const TUSSENPOOS_MS = 5 * 60_000;

/** Het scriptbestand dat dít document geladen heeft — de naam draagt het kenmerk van de inhoud. */
function eigenScript(): string | null {
  const script = document.querySelector('script[type="module"][src]');
  return script?.getAttribute('src') ?? null;
}

export function NieuweVersieMelding() {
  const { t } = useTranslation();
  const [nieuwe, setNieuwe] = useState<string | null>(null);
  const [weggeklikt, setWeggeklikt] = useState<string | null>(null);

  useEffect(() => {
    // Tijdens het ontwikkelen bestaan er geen namen met een kenmerk; dan valt er niets te melden.
    if (!import.meta.env.PROD) return undefined;

    const eigen = eigenScript();
    if (eigen === null) return undefined;

    let gestopt = false;

    const kijk = async () => {
      // Een vergeten tabblad op de achtergrond hoort geen verkeer te maken.
      if (document.visibilityState !== 'visible') return;

      try {
        const antwoord = await fetch(VERSIEBESTAND_PAD, { cache: 'no-store' });
        if (!antwoord.ok) return;

        const gegevens: unknown = await antwoord.json();
        const naam = (gegevens as { entry?: unknown } | null)?.entry;
        // Wat niet te lezen is, leidt tot niets. Een uitlevering van vóór deze wijziging heeft dit
        // bestandje niet, en een server die de startpagina zou terugsturen levert hier geen naam.
        if (typeof naam !== 'string' || naam === '') return;
        if (gestopt) return;

        if (naam === eigen) {
          // Terug bij de eigen versie — dat gebeurt tijdens een uitrol, wanneer twee containers om
          // beurten antwoorden. Ook het wegklikken vervalt dan: zonder dat zou een weggeklikte
          // versie die daarna terugkomt nooit meer gemeld worden.
          setNieuwe(null);
          setWeggeklikt(null);
          return;
        }
        setNieuwe(naam);
      } catch {
        // Een haperend netwerk is geen nieuwe versie.
      }
    };

    void kijk();
    const klok = setInterval(() => { void kijk(); }, TUSSENPOOS_MS);
    const bijZichtbaarWorden = () => { void kijk(); };
    document.addEventListener('visibilitychange', bijZichtbaarWorden);

    return () => {
      gestopt = true;
      clearInterval(klok);
      document.removeEventListener('visibilitychange', bijZichtbaarWorden);
    };
  }, []);

  // Wegklikken geldt voor dít tabblad en voor déze versie. Een ander tabblad draait zijn eigen
  // oude code en heeft de melding net zo hard nodig.
  if (nieuwe === null || nieuwe === weggeklikt) return null;

  return (
    <div
      data-testid="nieuwe-versie-melding"
      role="status"
      aria-live="polite"
      className={cn('fixed bottom-4 right-4 flex items-center gap-3 rounded-lg px-4 py-3 text-sm shadow-lg', LAGEN.melding)}
      style={{ backgroundColor: 'var(--primary)', color: '#fff' }}
    >
      <span
        className="material-symbols-outlined"
        style={{ fontSize: '18px' }}
        aria-hidden="true"
      >
        refresh
      </span>
      <span>{t('appVersion.newVersion')}</span>
      <button
        data-testid="nieuwe-versie-vernieuwen"
        onClick={() => window.location.reload()}
        className="rounded px-2 py-0.5 text-xs font-semibold transition-colors hover:bg-white/20"
        style={{ border: '1px solid rgba(255,255,255,0.4)' }}
      >
        {t('appVersion.refresh')}
      </button>
      <button
        data-testid="nieuwe-versie-sluiten"
        aria-label={t('appVersion.dismiss')}
        onClick={() => setWeggeklikt(nieuwe)}
        className="material-symbols-outlined transition-opacity hover:opacity-70"
        style={{ fontSize: '18px' }}
      >
        close
      </button>
    </div>
  );
}
